<template>
  <SnackbarAlert />
  <ErroConexao v-if="mostrarErroConexao" />
  <v-container v-else class="py-12">
    <TitleHeader>{{ titulo }}</TitleHeader>

    <v-row class="w-100 max-width mx-auto" align="start" justify="center" no-gutters>
      <v-col cols="12" sm="12" md="11" lg="10" xl="9">
        <v-card elevation="10" class="pa-6 rounded-xl bg-form-card border-left-institucional">

          <!-- Slot condicional para descrição -->
          <slot name="form-description" />

          <template v-if="loading">
            <LoadingState text="Carregando..." />
          </template>

          <template v-else-if="mostrarErroConexao">
            <p class="status-message error">Erro de conexão. Verifique sua internet.</p>
          </template>

          <template v-else-if="membros.length === 0">
            <div class="empty-state">
              <div class="empty-icon" aria-hidden="true">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                  <path fill="currentColor"
                    d="M6 2a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6H6m7 1.5L18.5 9H13V3.5M8 13h8v2H8v-2m0 4h8v2H8v-2m0-8h5v2H8V9Z" />
                </svg>
              </div>
              <h2 class="empty-title">Nenhum resultado encontrado</h2>
              <p class="empty-description">
                Ainda não há registros disponíveis nesta seção<br />
              </p>
            </div>
          </template>

          <template v-else>
            <MembrosLista :membros="membros" />
          </template>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script setup lang="ts">
import { ref, reactive, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import TitleHeader from '@/components/TitleHeader.vue'
import MembrosLista from '@/components/Membros.vue'
import ErroConexao from '@/components/ErroConexao.vue'
import { useMainStore } from '@/stores/index'
import LoadingState from '@/components/LoadingState.vue'

interface Membro {
  [key: string]: string
}

const props = defineProps<{
  slug: string
  titulo: string
}>()

const config = useRuntimeConfig()
const baseUrl = config.public.backofficeApiBaseUrl
const router = useRouter()
const mainStore = useMainStore()

const membros = ref<Membro[]>([])
const loading = ref(true)
const mostrarErroConexao = ref(false)
const fieldErrors = reactive<Record<string, string>>({})

function mostrarSnackbar(message: string, type: 'success' | 'error' = 'error') {
  mainStore.snackbar = {
    text: message,
    color: type === 'error' ? 'red-darken-2' : 'green-darken-2',
    variant: 'elevated',
    display: true,
    link: null
  }
}

function parseDescricao(descricaoHtml: string): Membro[] {
  const membrosParseados: Membro[] = []
  const parser = new DOMParser()
  const doc = parser.parseFromString(descricaoHtml, 'text/html')
  const elementos = Array.from(doc.querySelectorAll('p'))

  let membroAtual: Membro = {}
  for (const el of elementos) {
    const texto = el.textContent?.trim() || ''
    const match = texto.match(/^\[(\d+)\](.*)$/)
    if (!match) continue
    const chave = match[1]
    const valor = match[2].trim()

    if (chave === '1' && Object.keys(membroAtual).length) {
      membrosParseados.push(membroAtual)
      membroAtual = {}
    }

    membroAtual[chave] = valor
  }

  if (Object.keys(membroAtual).length) {
    membrosParseados.push(membroAtual)
  }

  return membrosParseados
}

onMounted(async () => {
  try {
    loading.value = true
    mostrarErroConexao.value = false

    const res = await fetch(`${baseUrl}/portal/paginas/buscar-pagina?slug=${props.slug}`)
    if (!res.ok) {
      const error = new Error('Erro HTTP')
      // @ts-ignore
      error.response = { status: res.status, _data: null }
      throw error
    }

    const data = await res.json()
    membros.value = parseDescricao(data.descricao || '')

  } catch (e: any) {
    if (e?.response?.status === 401) {
      router.push('/manutencao/acesso-negado')
    } else if (e?.response?.status === 404) {
      mostrarSnackbar('Página não encontrada', 'error')
    } else if (e.message?.includes('Failed to fetch')) {
      mostrarSnackbar('Erro de conexão. Verifique sua internet.', 'error')
      mostrarErroConexao.value = true
    } else {
      mostrarSnackbar(e.message || 'Erro inesperado ao carregar os dados.', 'error')
      mostrarErroConexao.value = true
    }
    membros.value = []
  } finally {
    loading.value = false
  }
})

onBeforeUnmount(() => {
  mainStore.resetSnackbar()
})
</script>

<style scoped>
.max-width {
  max-width: 1200px;
}

.bg-form-card {
  background-color: rgba(255, 255, 255, 0.92);
}

.border-left-institucional {
  border-left: 6px solid var(--funarpen-blue);
}

.empty-state {
  text-align: center;
  color: rgba(11, 69, 113, 0.85);
}

.empty-icon svg {
  width: 72px;
  height: 72px;
  fill: rgba(11, 69, 113, 0.6);
}

.empty-title {
  font-size: 1.75rem;
  font-weight: 700;
  margin-bottom: 0.75rem;
}

.empty-description {
  font-size: 1.125rem;
  font-weight: 400;
  color: rgba(11, 69, 113, 0.75);
  line-height: 1.6;
}
</style>
